import { Component } from '@theme/component';

const WEEKDAY_HEADER_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_FULL_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
// Wide enough window for the calendar to support a couple of months of
// forward navigation, without being so large the API call gets slow.
const CALENDAR_WINDOW_DAYS = 60;
// Read back by the Worker's orders/paid webhook to find the stored booking.
const INTAKE_TOKEN_PROPERTY = '_Nuvivo Intake Token';

function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char],
  );
}

/** Date of birth is typed as DD/MM/YYYY. Keeps only digits, adds the slashes
 * and stops at 8 digits, so the year can never run past 4 digits. An
 * autofilled ISO date (YYYY-MM-DD) is converted first. */
function formatDobInput(value) {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const digits = (iso ? iso[3] + iso[2] + iso[1] : value).replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
}

/** "DD/MM/YYYY" becomes "YYYY-MM-DD", or null unless it is a real date
 * between 1900 and today. */
function parseDob(text) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  const real = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  if (!real || year < 1900 || date > new Date()) return null;
  return match[3] + '-' + match[2] + '-' + match[1];
}

/** Local (not UTC) calendar-day key — grouping/navigation must match the
 * date the shopper actually sees on their own clock, not the UTC date the
 * API happens to serialise the slot under. */
function localDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

function parseDateKey(key) {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDateHeading(date) {
  return `${WEEKDAY_FULL_LABELS[date.getDay()]} ${date.getDate()} ${MONTH_LABELS[date.getMonth()]}`;
}

/** "Sat, 31 Oct" — the small heading above the list of times. */
function formatSlotsHeading(date) {
  const weekday = WEEKDAY_HEADER_LABELS[(date.getDay() + 6) % 7];
  return `${weekday}, ${String(date.getDate()).padStart(2, '0')} ${MONTH_LABELS[date.getMonth()].slice(0, 3)}`;
}

function formatSlotTime(isoString) {
  const date = new Date(isoString);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function formatSummaryDateTime(isoString) {
  const date = new Date(isoString);
  return `${formatDateHeading(date)} · ${formatSlotTime(isoString)}`;
}

const LONDON_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function londonParts(isoString) {
  const parts = Object.fromEntries(LONDON_PARTS.formatToParts(new Date(isoString)).map((p) => [p.type, p.value]));
  return { date: `${parts.day}-${parts.month}-${parts.year}`, time: `${parts.hour}:${parts.minute}` };
}

/** Cart-line properties that show the appointment in the cart, at checkout and
 * on the Shopify order (UK time, like the booking itself). Empty for routes
 * without a fixed appointment. */
function appointmentProperties(slot, clinic) {
  if (!slot) return {};
  const start = londonParts(slot.start);
  const end = londonParts(slot.end);
  const properties = { Date: start.date, Time: `${start.time} - ${end.time}` };
  if (clinic?.name) properties.Clinic = clinic.address ? `${clinic.name}, ${clinic.address}` : clinic.name;
  return properties;
}

/**
 * Inline sample-collection method selector. The widget itself is the
 * variant picker — clicking a method button selects that Shopify variant
 * directly (no dependency on a separate native variant picker), then
 * walks the shopper through whatever that route needs (an appointment
 * slot, a nearby clinic, a GP surgery name) before revealing the patient
 * registration form.
 *
 * Livingston and partner-clinic appointment slots are chosen in a
 * calendar pop-up (a nested `<dialog-component>`, not this component
 * itself) rather than inline — on:click bindings for elements inside that
 * nested dialog must be prefixed with `nuvivo-booking-component/`, since
 * the framework's event delegation otherwise resolves to the closest
 * `-component` ancestor, which would be the dialog, not this component.
 */
export class NuvivoBooking extends Component {
  requiredRefs = ['routeStep', 'bookingDialog'];

  /** @type {string | null} */
  #route = null;
  /** @type {string | null} */
  #selectedVariantId = null;
  /** @type {string | null} */
  #selectedVariantTitle = null;
  /** @type {string | null} */
  #selectedVariantSku = null;
  /** The paired "+ doctor's comments" variant for the chosen method, if the
   * product has one. @type {{ id: string, title: string, sku: string, extra: string } | null} */
  #commentsVariant = null;
  /** @type {{ start: string, end: string } | null} */
  #selectedSlot = null;
  /** @type {string | null} */
  #selectedClinicId = null;
  #postcodeSearchTimer;

  /** @type {{ kind: 'livingston' | 'partner', name: string, address: string, clinicId?: string, note?: string } | null} */
  #calendarContext = null;
  /** @type {Map<string, Array<{ start: string, end: string }>>} */
  #calendarSlotsByDate = new Map();
  /** @type {Date | null} */
  #calendarViewMonth = null;
  /** @type {string | null} */
  #calendarSelectedDate = null;
  /** @type {{ start: string, end: string } | null} */
  #calendarSelectedSlot = null;

  get apiBaseUrl() {
    return (this.dataset.apiBaseUrl ?? '').replace(/\/$/, '');
  }

  async #apiFetch(path) {
    const response = await fetch(`${this.apiBaseUrl}${path}`);
    if (!response.ok) {
      throw new Error(`request to ${path} failed with status ${response.status}`);
    }
    return response.json();
  }

  /** The pop-up's contents belong to the nested dialog-component, so they
   * aren't in this component's own `refs` — look them up directly. */
  #el(name) {
    return this.refs.bookingDialog.querySelector(`[ref="${name}"]`);
  }

  #showError(message) {
    const el = this.#el('errorMessage');
    el.hidden = false;
    el.textContent = message;
  }

  #hideError() {
    const el = this.#el('errorMessage');
    el.hidden = true;
    el.textContent = '';
  }

  #dialogContentEl() {
    return this.#el('dialogContent');
  }

  get #needsAppointment() {
    return this.#route === 'livingston' || this.#route === 'partner';
  }

  /** Step 1 of the pop-up: the appointment calendar. */
  /** The base variant is the "Laboratory report only" one; ticking the
   * doctor's-comments box swaps to its paired "+ doctor's comments" variant. */
  #chosenVariant(withComments) {
    if (withComments && this.#commentsVariant) return this.#commentsVariant;
    return {
      id: this.#selectedVariantId,
      title: this.#selectedVariantTitle,
      sku: this.#selectedVariantSku ?? '',
    };
  }

  #showAppointmentStep() {
    this.#el('detailsForm').hidden = true;
    this.#dialogContentEl().hidden = false;
    this.#scrollDialogToTop();
  }

  /** Step 2 of the pop-up: patient details. Opens the pop-up if needed. */
  #showDetailsStep() {
    this.#dialogContentEl().hidden = true;
    this.#el('detailsForm').hidden = false;
    this.#el('gpStep').hidden = this.#route !== 'home';

    // The partner network (Tuli) needs a mobile number and the patient's
    // ethnicity; no other route asks for them.
    const isPartner = this.#route === 'partner';
    this.#el('ethnicityRow').hidden = !isPartner;
    const ethnicity = this.#el('ethnicity');
    ethnicity.disabled = !isPartner;
    ethnicity.required = isPartner;
    const phone = this.#el('phone');
    phone.required = isPartner;
    phone.placeholder = isPartner ? 'Mobile number' : 'Phone (optional)';
    phone.setAttribute('aria-label', phone.placeholder);
    this.#el('backButton').hidden = !this.#needsAppointment;

    // Date of birth: type-as-you-go DD/MM/YYYY, wired once.
    const dob = this.#el('dob');
    if (!dob.dataset.wired) {
      dob.dataset.wired = '1';
      dob.addEventListener('input', () => {
        dob.value = formatDobInput(dob.value);
        dob.setCustomValidity(
          dob.value.length < 10 || parseDob(dob.value) ? '' : 'Enter a real date of birth as DD/MM/YYYY.',
        );
      });
    }

    // Only offered where the product has a paired "+ doctor's comments"
    // variant for this collection method.
    const commentsRow = this.#el('commentsRow');
    commentsRow.hidden = !this.#commentsVariant;
    if (this.#commentsVariant) {
      this.#el('commentsLabel').textContent = `Add doctor's comments (+${this.#commentsVariant.extra})`;
    }

    const appointmentLine = this.#el('detailsAppointment');
    if (this.#selectedSlot && this.#calendarContext) {
      appointmentLine.hidden = false;
      appointmentLine.textContent = `${this.#calendarContext.name} · ${formatSummaryDateTime(this.#selectedSlot.start)}`;
    } else {
      appointmentLine.hidden = true;
      appointmentLine.textContent = '';
    }

    this.#hideError();
    this.refs.bookingDialog.showDialog();
    this.#scrollDialogToTop();
  }

  /** The pop-up scrolls as one page on a phone, so switching steps would
   * otherwise leave the form scrolled to wherever the calendar was. */
  #scrollDialogToTop() {
    const dialog = this.#el('dialog');
    if (!dialog) return;
    dialog.scrollTop = 0;
    requestAnimationFrame(() => {
      dialog.scrollTop = 0;
    });
  }

  selectMethod(event) {
    const button = event.target;
    if (button.disabled) return;

    this.querySelectorAll('.nuvivo-booking__method').forEach((el) => el.classList.remove('is-selected'));
    button.classList.add('is-selected');

    // Re-clicking the method that's already chosen reopens the pop-up where
    // the shopper left off instead of wiping what they've entered.
    if (button.dataset.variantId === this.#selectedVariantId && this.#route) {
      if (this.#needsAppointment && !this.#selectedSlot) {
        this.#renderRouteStep();
      } else {
        this.#showDetailsStep();
      }
      return;
    }

    this.#route = button.dataset.route;
    this.#selectedVariantId = button.dataset.variantId;
    this.#selectedVariantTitle = button.dataset.variantTitle;
    this.#selectedVariantSku = button.dataset.variantSku;
    this.#commentsVariant = button.dataset.commentsVariantId
      ? {
          id: button.dataset.commentsVariantId,
          title: button.dataset.commentsVariantTitle ?? '',
          sku: button.dataset.commentsVariantSku ?? '',
          extra: button.dataset.commentsExtra ?? '',
        }
      : null;
    this.#selectedSlot = null;
    this.#selectedClinicId = null;
    this.#calendarContext = null;

    this.#el('detailsForm').reset();
    this.#el('submitButton').disabled = false;
    this.#el('submitButton').textContent = 'Confirm booking details';
    this.#hideError();

    this.#renderRouteStep();
  }

  #renderRouteStep() {
    const container = this.refs.routeStep;
    container.innerHTML = '';

    if (this.#route === 'livingston') {
      container.hidden = true;
      this.#openBookingDialog({
        kind: 'livingston',
        name: this.dataset.livingstonName || 'Nuvivo Livingston',
        address: this.dataset.livingstonAddress || '',
      });
      return;
    }

    if (this.#route === 'partner') {
      container.hidden = false;
      container.innerHTML = `
        <legend>Find a clinic near you</legend>
        <div class="field">
          <input
            type="text"
            ref="postcodeInput"
            class="field__input"
            placeholder="Enter your postcode"
            aria-label="Postcode"
            on:input="/handlePostcodeInput"
            autocomplete="postal-code"
            autocapitalize="characters"
            autocorrect="off"
            spellcheck="false"
          >
        </div>
        <div ref="clinicList" class="nuvivo-booking__clinics"></div>
      `;
      return;
    }

    // No appointment to pick for this route — go straight to patient details.
    container.hidden = true;
    this.#showDetailsStep();
  }

  /** Debounced: as soon as the shopper has typed something that looks like
   * a usable postcode, automatically fetch the nearest clinics — no
   * separate "Search" button/step. */
  handlePostcodeInput(event) {
    clearTimeout(this.#postcodeSearchTimer);
    const postcode = event.target.value.trim();
    if (postcode.replace(/\s/g, '').length < 5) return;
    this.#postcodeSearchTimer = setTimeout(() => this.#findNearestClinics(postcode), 500);
  }

  /** Fetches the nearest clinics (already ranked and capped server-side —
   * see GET /clinics). Picking one opens the booking pop-up for that
   * clinic; availability isn't loaded inline for all of them up front. */
  async #findNearestClinics(postcode) {
    const clinicList = this.refs.routeStep.querySelector('[ref="clinicList"]');
    if (!clinicList) return;

    clinicList.innerHTML = '<p>Finding nearby clinics…</p>';
    try {
      const data = await this.#apiFetch(`/clinics?postcode=${encodeURIComponent(postcode)}`);
      // Nuvivo's own clinic is in the list too; offer it only when this
      // product can actually be booked there.
      const livingstonButton = this.querySelector('.nuvivo-booking__method[data-route="livingston"]');
      const clinics = (data.clinics ?? []).filter(
        (clinic) => clinic.id !== 'livingston' || (livingstonButton && !livingstonButton.disabled),
      );
      if (clinics.length === 0) {
        clinicList.innerHTML = '<p>No clinics found near that postcode.</p>';
        return;
      }

      clinicList.innerHTML = clinics
        .map((clinic) => {
          const isLivingston = clinic.id === 'livingston';
          const name = isLivingston ? this.dataset.livingstonName || clinic.name : clinic.name;
          const address = isLivingston
            ? this.dataset.livingstonAddress || `${clinic.address}, ${clinic.postcode}`
            : `${clinic.address}, ${clinic.postcode}`;
          return `
            <button
              type="button"
              class="nuvivo-booking__clinic"
              on:click="/openClinicDialog"
              data-clinic-id="${escapeHtml(clinic.id)}"
              data-clinic-name="${escapeHtml(name)}"
              data-clinic-address="${escapeHtml(isLivingston ? address : clinic.address)}"
              data-clinic-postcode="${escapeHtml(isLivingston ? '' : clinic.postcode)}"
            >
              <p class="nuvivo-booking__clinic-name">${escapeHtml(name)} — ${clinic.distanceMiles} mi</p>
              <p class="nuvivo-booking__clinic-address">${escapeHtml(address)}</p>
            </button>
          `;
        })
        .join('');
    } catch {
      clinicList.innerHTML = '<p>Could not search clinics. Please try again later.</p>';
    }
  }

  openClinicDialog(event) {
    const { clinicId, clinicName, clinicAddress, clinicPostcode } = event.target.dataset;
    if (clinicId === 'livingston') {
      // Nuvivo's own clinic isn't a partner clinic: it's booked through the
      // Livingston method (its own variant, price and calendar).
      const livingstonButton = this.querySelector('.nuvivo-booking__method[data-route="livingston"]');
      if (livingstonButton && !livingstonButton.disabled) this.selectMethod({ target: livingstonButton });
      return;
    }
    this.#openBookingDialog({
      kind: 'partner',
      clinicId,
      name: clinicName,
      address: `${clinicAddress}, ${clinicPostcode}`,
      note: 'Appointments shown allow sufficient time for your collection kit to arrive.',
    });
  }

  /** Opens the shared calendar pop-up for either Livingston or a specific
   * partner clinic, fetches a wide window of availability, and groups it
   * by local calendar day for the month grid. */
  async #openBookingDialog(context) {
    this.#calendarContext = context;
    this.#calendarSlotsByDate = new Map();
    this.#calendarViewMonth = null;
    this.#calendarSelectedDate = null;
    this.#calendarSelectedSlot = null;

    this.#showAppointmentStep();
    const content = this.#dialogContentEl();
    content.innerHTML = `
      <div class="nuvivo-booking__dialog-header nuvivo-booking__dialog-header--calendar">
        <h2 id="nuvivo-booking-dialog-heading">Select a date &amp; time</h2>
        <p class="nuvivo-booking__dialog-address"><strong>${escapeHtml(context.name)}</strong> · ${escapeHtml(context.address)}</p>
      </div>
      <p class="nuvivo-booking__dialog-status">Loading availability…</p>
    `;
    this.refs.bookingDialog.showDialog();

    const path =
      context.kind === 'livingston'
        ? `/semble/livingston-availability?days=${CALENDAR_WINDOW_DAYS}`
        : `/availability?clinic_id=${encodeURIComponent(context.clinicId)}&days=${CALENDAR_WINDOW_DAYS}`;

    try {
      const data = await this.#apiFetch(path);
      const slots = data.slots ?? [];
      if (slots.length === 0) {
        const status = content.querySelector('.nuvivo-booking__dialog-status');
        if (status) status.textContent = 'No availability found. Please try again later.';
        return;
      }

      for (const slot of slots) {
        const key = localDateKey(new Date(slot.start));
        const list = this.#calendarSlotsByDate.get(key) ?? [];
        list.push(slot);
        this.#calendarSlotsByDate.set(key, list);
      }

      const firstDate = [...this.#calendarSlotsByDate.keys()].sort()[0];
      this.#calendarViewMonth = parseDateKey(firstDate);
      this.#calendarViewMonth.setDate(1);
      this.#calendarSelectedDate = firstDate;
      this.#renderCalendar();
    } catch {
      const status = content.querySelector('.nuvivo-booking__dialog-status');
      if (status) status.textContent = 'Could not load availability. Please try again later.';
    }
  }

  #renderCalendar() {
    const context = this.#calendarContext;
    const viewMonth = this.#calendarViewMonth;
    const content = this.#dialogContentEl();
    if (!context || !viewMonth || !content) return;

    const monthLabel = `${MONTH_LABELS[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`;
    const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startWeekday = (firstOfMonth.getDay() + 6) % 7; // 0 = Monday
    const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();

    const sortedKeys = [...this.#calendarSlotsByDate.keys()].sort();
    const minMonth = parseDateKey(sortedKeys[0]);
    const maxMonth = parseDateKey(sortedKeys.at(-1));
    const viewMonthIndex = viewMonth.getFullYear() * 12 + viewMonth.getMonth();
    const canGoPrev = viewMonthIndex > minMonth.getFullYear() * 12 + minMonth.getMonth();
    const canGoNext = viewMonthIndex < maxMonth.getFullYear() * 12 + maxMonth.getMonth();

    let dayCells = '';
    for (let i = 0; i < startWeekday; i++) {
      dayCells += '<span class="nuvivo-booking__calendar-day"></span>';
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const key = `${viewMonth.getFullYear()}-${String(viewMonth.getMonth() + 1).padStart(2, '0')}-${String(
        day,
      ).padStart(2, '0')}`;
      const hasSlots = this.#calendarSlotsByDate.has(key);
      if (hasSlots) {
        const isSelected = key === this.#calendarSelectedDate;
        dayCells += `
          <button
            type="button"
            class="nuvivo-booking__calendar-day nuvivo-booking__calendar-day--available${isSelected ? ' is-selected' : ''}"
            on:click="nuvivo-booking-component/selectCalendarDay"
            data-date="${key}"
          >${day}</button>
        `;
      } else {
        dayCells += `<span class="nuvivo-booking__calendar-day">${day}</span>`;
      }
    }

    const selectedSlots = this.#calendarSelectedDate
      ? (this.#calendarSlotsByDate.get(this.#calendarSelectedDate) ?? [])
      : [];
    const selectedDateHeading = this.#calendarSelectedDate
      ? formatSlotsHeading(parseDateKey(this.#calendarSelectedDate))
      : 'Select a date';
    const selectedIndex = this.#calendarSelectedDate ? sortedKeys.indexOf(this.#calendarSelectedDate) : -1;
    const canGoPrevDate = selectedIndex > 0;
    const canGoNextDate = selectedIndex >= 0 && selectedIndex < sortedKeys.length - 1;

    content.innerHTML = `
      <div class="nuvivo-booking__dialog-header nuvivo-booking__dialog-header--calendar">
        <h2 id="nuvivo-booking-dialog-heading">Select a date &amp; time</h2>
        <p class="nuvivo-booking__dialog-address"><strong>${escapeHtml(context.name)}</strong> · ${escapeHtml(context.address)}</p>
      </div>
      <div class="nuvivo-booking__dialog-body">
        <div class="nuvivo-booking__calendar">
          <div class="nuvivo-booking__calendar-nav">
            <span class="nuvivo-booking__calendar-month">${monthLabel}</span>
            <span class="nuvivo-booking__calendar-arrows">
              <button
                type="button"
                class="nuvivo-booking__calendar-nav-button"
                on:click="nuvivo-booking-component/calendarPrevMonth"
                ${canGoPrev ? '' : 'disabled'}
                aria-label="Previous month"
              >‹</button>
              <button
                type="button"
                class="nuvivo-booking__calendar-nav-button"
                on:click="nuvivo-booking-component/calendarNextMonth"
                ${canGoNext ? '' : 'disabled'}
                aria-label="Next month"
              >›</button>
            </span>
          </div>
          <div class="nuvivo-booking__calendar-weekdays">
            ${WEEKDAY_HEADER_LABELS.map((label) => `<span>${label}</span>`).join('')}
          </div>
          <div class="nuvivo-booking__calendar-days">${dayCells}</div>
        </div>
        <div class="nuvivo-booking__calendar-slots">
          <div class="nuvivo-booking__calendar-slots-header">
            <span class="nuvivo-booking__calendar-month">${escapeHtml(selectedDateHeading)}</span>
            <span class="nuvivo-booking__calendar-arrows">
              <button
                type="button"
                class="nuvivo-booking__calendar-nav-button"
                on:click="nuvivo-booking-component/selectPreviousDate"
                ${canGoPrevDate ? '' : 'disabled'}
                aria-label="Previous day with times"
              >‹</button>
              <button
                type="button"
                class="nuvivo-booking__calendar-nav-button"
                on:click="nuvivo-booking-component/selectNextDate"
                ${canGoNextDate ? '' : 'disabled'}
                aria-label="Next day with times"
              >›</button>
            </span>
          </div>
          <div class="nuvivo-booking__calendar-slot-list">
            ${selectedSlots
              .map(
                (slot) => `
                  <button
                    type="button"
                    class="nuvivo-booking__calendar-slot${this.#calendarSelectedSlot?.start === slot.start ? ' is-selected' : ''}"
                    on:click="nuvivo-booking-component/selectCalendarSlot"
                    data-start="${slot.start}"
                    data-end="${slot.end}"
                  >${formatSlotTime(slot.start)}</button>
                `,
              )
              .join('')}
          </div>
          ${context.note ? `<p class="nuvivo-booking__calendar-note">${escapeHtml(context.note)}</p>` : ''}
        </div>
      </div>
      <div class="nuvivo-booking__dialog-footer">
        <button
          type="button"
          class="button nuvivo-booking__dialog-next"
          on:click="nuvivo-booking-component/confirmCalendarSelection"
          ${this.#calendarSelectedSlot ? '' : 'disabled'}
        >Confirm</button>
      </div>
    `;
  }

  /** The dialog closes itself on any click whose target is no longer inside
   * it. Re-rendering synchronously inside a click handler detaches the
   * clicked button before that check runs, so the dialog would close on
   * every calendar interaction — defer the re-render until the click event
   * has finished dispatching. */
  #rerenderCalendarAfterClick() {
    setTimeout(() => this.#renderCalendar(), 0);
  }

  selectCalendarDay(event) {
    this.#calendarSelectedDate = event.target.dataset.date;
    this.#calendarSelectedSlot = null;
    this.#rerenderCalendarAfterClick();
  }

  /** The arrows beside the day heading move to the previous / next day that
   * has times, switching the month shown on the left when needed. */
  #shiftSelectedDate(step) {
    const keys = [...this.#calendarSlotsByDate.keys()].sort();
    const next = keys[keys.indexOf(this.#calendarSelectedDate) + step];
    if (!next) return;
    this.#calendarSelectedDate = next;
    this.#calendarSelectedSlot = null;
    const date = parseDateKey(next);
    this.#calendarViewMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    this.#rerenderCalendarAfterClick();
  }

  selectPreviousDate() {
    this.#shiftSelectedDate(-1);
  }

  selectNextDate() {
    this.#shiftSelectedDate(1);
  }

  /** Picking a time only marks that pill and enables Confirm — the calendar is
   * not rebuilt, so the list keeps its scroll position (a rebuild would send
   * it back to the top, hiding the time just chosen). */
  selectCalendarSlot(event) {
    const button = event.target.closest('button');
    if (!button) return;
    this.#calendarSelectedSlot = { start: button.dataset.start, end: button.dataset.end };

    const content = this.#dialogContentEl();
    content?.querySelectorAll('.nuvivo-booking__calendar-slot').forEach((pill) => {
      pill.classList.toggle('is-selected', pill === button);
    });
    const confirmButton = content?.querySelector('.nuvivo-booking__dialog-next');
    if (confirmButton) confirmButton.disabled = false;
  }

  calendarPrevMonth() {
    this.#calendarViewMonth = new Date(
      this.#calendarViewMonth.getFullYear(),
      this.#calendarViewMonth.getMonth() - 1,
      1,
    );
    this.#rerenderCalendarAfterClick();
  }

  calendarNextMonth() {
    this.#calendarViewMonth = new Date(
      this.#calendarViewMonth.getFullYear(),
      this.#calendarViewMonth.getMonth() + 1,
      1,
    );
    this.#rerenderCalendarAfterClick();
  }

  confirmCalendarSelection() {
    if (!this.#calendarSelectedSlot) return;

    this.#selectedSlot = this.#calendarSelectedSlot;
    if (this.#calendarContext.kind === 'partner') {
      this.#selectedClinicId = this.#calendarContext.clinicId;
    }

    this.#renderSelectedSummary();
    this.#showDetailsStep();
  }

  backToAppointment() {
    this.#showAppointmentStep();
    if (this.#calendarSlotsByDate.size > 0) {
      this.#renderCalendar();
    } else if (this.#calendarContext) {
      this.#openBookingDialog(this.#calendarContext);
    }
  }

  /** Shows a compact summary of the chosen appointment on the page, so the
   * shopper can reopen the pop-up (to change it or carry on) if they close it. */
  #renderSelectedSummary() {
    const context = this.#calendarContext;
    const container = this.refs.routeStep;
    container.hidden = false;
    container.innerHTML = `
      <div class="nuvivo-booking__summary">
        <div class="nuvivo-booking__summary-text">
          <p class="nuvivo-booking__kicker">Selected appointment</p>
          <p class="nuvivo-booking__summary-name">${escapeHtml(context.name)}</p>
          <p class="nuvivo-booking__summary-address">${escapeHtml(context.address)}</p>
          <p class="nuvivo-booking__summary-datetime">${escapeHtml(formatSummaryDateTime(this.#selectedSlot.start))}</p>
        </div>
        <div class="nuvivo-booking__summary-actions">
          <button type="button" class="button" on:click="/continueToDetails">Enter patient details</button>
          <button type="button" class="button button-secondary" on:click="/changeAppointment">Change appointment</button>
        </div>
      </div>
    `;
  }

  changeAppointment() {
    if (this.#calendarContext) this.#openBookingDialog(this.#calendarContext);
  }

  continueToDetails() {
    this.#showDetailsStep();
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.#hideError();

    if (!this.#route || !this.#selectedVariantId) {
      this.#showError('Please choose how you would like to test.');
      return;
    }
    if ((this.#route === 'livingston' || this.#route === 'partner') && !this.#selectedSlot) {
      this.#showError('Please choose an appointment slot.');
      return;
    }
    if (this.#route === 'partner' && !this.#selectedClinicId) {
      this.#showError('Please choose a clinic.');
      return;
    }

    const formData = new FormData(this.#el('detailsForm'));
    const dobIso = parseDob(String(formData.get('dob') ?? ''));
    if (!dobIso) {
      this.#showError('Please enter your date of birth as DD/MM/YYYY.');
      return;
    }
    const answers = [];
    const notes = formData.get('notes');
    if (notes) answers.push({ questionId: 'notes', value: String(notes) });
    const gpSurgery = formData.get('gpSurgery');
    if (gpSurgery) answers.push({ questionId: 'gp_practice', value: String(gpSurgery) });

    const variant = this.#chosenVariant(formData.get('doctorComments') === '1');

    const payload = {
      productId: this.dataset.productId,
      variantId: variant.id,
      variantTitle: variant.title,
      variantSku: variant.sku,
      inuviCode: this.dataset.inuviCode || undefined,
      sembleCode: this.dataset.sembleCode || undefined,
      route: this.#route,
      questionnaireId: this.dataset.sembleQuestionnaireId,
      patient: {
        firstName: String(formData.get('firstName') ?? ''),
        lastName: String(formData.get('lastName') ?? ''),
        email: String(formData.get('email') ?? ''),
        dob: dobIso,
        sex: formData.get('sex') ? String(formData.get('sex')) : undefined,
        ethnicity: formData.get('ethnicity') ? String(formData.get('ethnicity')) : undefined,
        phone: formData.get('phone') ? String(formData.get('phone')) : undefined,
      },
      address: {
        line1: String(formData.get('addressLine1') ?? ''),
        line2: formData.get('addressLine2') ? String(formData.get('addressLine2')) : undefined,
        city: String(formData.get('addressCity') ?? ''),
        postcode: String(formData.get('addressPostcode') ?? ''),
        country: 'United Kingdom',
      },
      answers,
      selectedSlot: this.#selectedSlot ?? undefined,
      clinicId: this.#selectedClinicId ?? undefined,
      // Shown on the Semble booking so staff can see where a partner-clinic
      // sample is taken.
      clinicName: this.#route === 'partner' ? this.#calendarContext?.name : undefined,
      clinicAddress: this.#route === 'partner' ? this.#calendarContext?.address : undefined,
    };

    this.#el('submitButton').disabled = true;
    this.#el('submitButton').textContent = 'Submitting…';

    try {
      const response = await fetch(`${this.apiBaseUrl}/intake`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error ?? `request failed with status ${response.status}`);
      }
      const result = await response.json();
      await this.#addToCart(
        variant.id,
        result.intakeToken,
        appointmentProperties(this.#selectedSlot, this.#route === 'partner' ? this.#calendarContext : null),
      );
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
      this.#el('submitButton').disabled = false;
      this.#el('submitButton').textContent = 'Confirm booking details';
    }
  }

  /** Puts the chosen variant in the cart with the intake token as a line
   * property (the Worker's orders/paid webhook reads it back), then goes to
   * the cart page, where the shopper can add more tests before checking out.
   * Uses Shopify's cart API directly rather than the theme's add-to-cart
   * form, which depends on that form's button state. Throws on failure so
   * the caller can show the message in the pop-up. */
  async #addToCart(variantId, intakeToken, appointment = {}) {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: { ...appointment, [INTAKE_TOKEN_PROPERTY]: intakeToken },
          },
        ],
      }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.description ?? body?.message ?? 'Could not add this booking to your cart. Please try again.');
    }
    window.location.assign(`${root}cart`);
  }
}

if (!customElements.get('nuvivo-booking-component')) {
  customElements.define('nuvivo-booking-component', NuvivoBooking);
}
